import { useState, useEffect, useRef } from 'react'
import { toast } from 'react-hot-toast'
import type { Producto } from '@/types/database'
import { TrashIcon, CameraIcon } from '@/components/shared/Icons'
import { uploadToR2 } from '@/lib/upload'
import {
  useCategorias, useDepositos, useCrearProducto, useActualizarProducto,
  useReglasCantidad, useGuardarRegla, useEliminarRegla,
  useIngredientes, useAgregarIngrediente, useEliminarIngrediente,
  useProductos,
  FORM_VACIO, getPrecio, type ProductoFormData,
} from './hooks'

interface Props {
  producto: Producto | null
  onClose: () => void
}

export default function ProductoDrawer({ producto, onClose }: Props) {
  const { data: categorias = [] } = useCategorias()
  const { data: depositos = [] } = useDepositos()
  const { data: todosProductos = [] } = useProductos()
  const crear = useCrearProducto()
  const actualizar = useActualizarProducto()
  const { data: reglas = [] } = useReglasCantidad(producto?.id)
  const guardarRegla = useGuardarRegla()
  const eliminarRegla = useEliminarRegla()
  const { data: ingredientes = [] } = useIngredientes(producto?.id)
  const agregarIngrediente = useAgregarIngrediente()
  const eliminarIngrediente = useEliminarIngrediente()

  const [form, setForm] = useState<ProductoFormData>(FORM_VACIO)
  const [fotoUrl, setFotoUrl] = useState<string | null>(null)
  const [uploadingFoto, setUploadingFoto] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const REGLA_VACIA: { cantidad_minima: string; tipo_precio: 'mayorista' | 'personalizado'; precio_especial: string; descripcion: string } = { cantidad_minima: '', tipo_precio: 'mayorista', precio_especial: '', descripcion: '' }
  const [reglaForm, setReglaForm] = useState(REGLA_VACIA)
  const [mostrarReglaForm, setMostrarReglaForm] = useState(false)

  const ING_VACIO = { ingrediente_id: '', cantidad: '1' }
  const [ingForm, setIngForm] = useState(ING_VACIO)
  const [mostrarIngForm, setMostrarIngForm] = useState(false)

  useEffect(() => {
    setFotoUrl(producto?.foto_url ?? null)
    if (producto) {
      setForm({
        nombre: producto.nombre,
        marca: producto.marca ?? '',
        codigo_barras: producto.codigo_barras ?? '',
        sku: producto.sku ?? '',
        categoria_id: producto.categoria_id ?? '',
        stock_minimo: producto.stock_minimo,
        precio_costo: getPrecio(producto, 'costo'),
        precio_minorista: getPrecio(producto, 'minorista'),
        precio_mayorista: getPrecio(producto, 'mayorista'),
      })
    } else {
      setForm(FORM_VACIO)
    }
  }, [producto])

  function set(field: keyof ProductoFormData, value: string | number) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  async function handleFotoChange(file: File) {
    setUploadingFoto(true)
    try {
      const url = await uploadToR2(file, 'productos')
      setFotoUrl(url)
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al subir imagen')
    } finally {
      setUploadingFoto(false)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.nombre.trim()) {
      toast.error('El nombre es obligatorio')
      return
    }

    try {
      if (producto) {
        const presentacionId = producto.producto_presentaciones?.[0]?.id ?? ''
        await actualizar.mutateAsync({ id: producto.id, form, presentacionId, fotoUrl })
        toast.success('Producto actualizado')
      } else {
        const depositoId = depositos[0]?.id ?? ''
        await crear.mutateAsync({ form, depositoId, fotoUrl })
        toast.success('Producto creado')
      }
      onClose()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar')
    }
  }

  async function handleGuardarRegla() {
    const cant = parseInt(reglaForm.cantidad_minima)
    if (!cant || cant < 1) { toast.error('Ingresá una cantidad mínima válida'); return }
    if (reglaForm.tipo_precio === 'personalizado' && !parseFloat(reglaForm.precio_especial)) {
      toast.error('Ingresá el precio especial'); return
    }
    try {
      await guardarRegla.mutateAsync({
        producto_id: producto!.id,
        cantidad_minima: cant,
        tipo_precio: reglaForm.tipo_precio,
        precio_especial: reglaForm.tipo_precio === 'personalizado' ? parseFloat(reglaForm.precio_especial) : null,
        descripcion: reglaForm.descripcion.trim() || null,
      })
      setReglaForm(REGLA_VACIA)
      setMostrarReglaForm(false)
      toast.success('Regla guardada')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar regla')
    }
  }

  async function handleEliminarRegla(id: string) {
    try {
      await eliminarRegla.mutateAsync({ id, productoId: producto!.id })
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al eliminar regla')
    }
  }

  async function handleAgregarIngrediente() {
    if (!ingForm.ingrediente_id) { toast.error('Seleccioná un ingrediente'); return }
    const cant = parseFloat(ingForm.cantidad)
    if (!cant || cant <= 0) { toast.error('Ingresá una cantidad válida'); return }
    try {
      await agregarIngrediente.mutateAsync({
        producto_id: producto!.id,
        ingrediente_id: ingForm.ingrediente_id,
        cantidad: cant,
      })
      setIngForm(ING_VACIO)
      setMostrarIngForm(false)
      toast.success('Ingrediente agregado')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al agregar ingrediente')
    }
  }

  async function handleEliminarIngrediente(id: string) {
    try {
      await eliminarIngrediente.mutateAsync({ id, productoId: producto!.id })
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al eliminar ingrediente')
    }
  }

  const isPending = crear.isPending || actualizar.isPending
  const titulo = producto ? 'Editar producto' : 'Nuevo producto'

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 40,
          background: 'rgba(0,0,0,0.28)',
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 41,
        width: 480,
        background: 'var(--me-surface)',
        boxShadow: '-20px 0 60px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--me-line)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{
            fontFamily: 'var(--me-display)',
            fontVariationSettings: '"opsz" 24, "wght" 700',
            fontSize: 18,
            letterSpacing: '-0.025em',
          }}>
            {titulo}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--me-text-3)', borderRadius: 8, padding: 6,
              display: 'flex', alignItems: 'center',
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {/* Foto */}
          <Section label="Imagen del producto">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFotoChange(f) }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {fotoUrl ? (
                <img
                  src={fotoUrl}
                  alt="Foto del producto"
                  style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--me-line)', flexShrink: 0 }}
                />
              ) : (
                <div style={{
                  width: 72, height: 72, borderRadius: 8, flexShrink: 0,
                  border: '1.5px dashed var(--me-line)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--me-surface-2)', color: 'var(--me-text-4)',
                }}>
                  <CameraIcon size={22} />
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button
                  type="button"
                  className="me-btn me-btn-ghost"
                  style={{ fontSize: 12, padding: '5px 12px' }}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFoto}
                >
                  {uploadingFoto ? 'Subiendo...' : fotoUrl ? 'Cambiar imagen' : 'Subir imagen'}
                </button>
                {fotoUrl && (
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11.5, color: 'var(--me-bad)', padding: 0, textAlign: 'left' }}
                    onClick={() => setFotoUrl(null)}
                  >
                    Quitar imagen
                  </button>
                )}
                <span style={{ fontSize: 11, color: 'var(--me-text-4)' }}>JPG, PNG o WebP · máx 2 MB</span>
              </div>
            </div>
          </Section>

          <Section label="Datos basicos">
            <Field label="Nombre *">
              <input
                id="prod-nombre"
                name="nombre"
                className="me-input"
                style={{ width: '100%' }}
                type="text"
                value={form.nombre}
                onChange={e => set('nombre', e.target.value)}
                placeholder="Nombre del producto"
                autoFocus
                autoComplete="off"
              />
            </Field>
            <Field label="Marca">
              <input
                id="prod-marca"
                name="marca"
                className="me-input"
                style={{ width: '100%' }}
                type="text"
                value={form.marca}
                onChange={e => set('marca', e.target.value)}
                placeholder="Marca (opcional)"
                autoComplete="off"
              />
            </Field>
            <TwoCol>
              <Field label="Codigo de barras">
                <input
                  id="prod-codigo-barras"
                  name="codigo_barras"
                  className="me-input"
                  style={{ width: '100%', fontFamily: 'var(--me-mono)', fontSize: 13 }}
                  type="text"
                  value={form.codigo_barras}
                  onChange={e => set('codigo_barras', e.target.value)}
                  placeholder="EAN / UPC"
                  autoComplete="off"
                />
              </Field>
              <Field label="SKU">
                <input
                  id="prod-sku"
                  name="sku"
                  className="me-input"
                  style={{ width: '100%', fontFamily: 'var(--me-mono)', fontSize: 13 }}
                  type="text"
                  value={form.sku}
                  onChange={e => set('sku', e.target.value)}
                  placeholder="SKU interno"
                  autoComplete="off"
                />
              </Field>
            </TwoCol>
            <TwoCol>
              <Field label="Categoria">
                <select
                  id="prod-categoria"
                  name="categoria_id"
                  className="me-input"
                  style={{ width: '100%' }}
                  value={form.categoria_id}
                  onChange={e => set('categoria_id', e.target.value)}
                >
                  <option value="">Sin categoria</option>
                  {categorias.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </Field>
              <Field label="Stock minimo">
                <input
                  id="prod-stock-minimo"
                  name="stock_minimo"
                  className="me-input"
                  style={{ width: '100%', fontFamily: 'var(--me-mono)', fontSize: 13 }}
                  type="number"
                  min="0"
                  value={form.stock_minimo}
                  onChange={e => set('stock_minimo', parseInt(e.target.value) || 0)}
                />
              </Field>
            </TwoCol>
          </Section>

          <Section label="Precios (Unidad)">
            <TwoCol>
              <Field label="Costo">
                <MoneyInput
                  name="precio_costo"
                  value={form.precio_costo}
                  onChange={v => set('precio_costo', v)}
                  placeholder="0,00"
                />
              </Field>
              <Field label="Minorista">
                <MoneyInput
                  name="precio_minorista"
                  value={form.precio_minorista}
                  onChange={v => set('precio_minorista', v)}
                  placeholder="0,00"
                />
              </Field>
            </TwoCol>
            <Field label="Mayorista">
              <MoneyInput
                name="precio_mayorista"
                value={form.precio_mayorista}
                onChange={v => set('precio_mayorista', v)}
                placeholder="0,00"
              />
            </Field>
          </Section>

          {producto && (
            <>
            <Section label="Precios por volumen">
              {reglas.length === 0 && !mostrarReglaForm && (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--me-text-3)', lineHeight: 1.5 }}>
                  Sin reglas — al agregar una, la venta aplicará precio mayorista (o personalizado) automáticamente cuando la cantidad supere el mínimo.
                </p>
              )}

              {reglas.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {reglas.map(r => (
                    <div key={r.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 10px', borderRadius: 8,
                      background: 'var(--me-surface-2)', border: '1px solid var(--me-line-2)',
                    }}>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, color: 'var(--me-text-3)' }}>≥</span>
                        <span style={{ fontFamily: 'var(--me-mono)', fontSize: 13, fontWeight: 700, color: 'var(--me-text)', fontFeatureSettings: '"tnum"' }}>
                          {r.cantidad_minima} unid.
                        </span>
                        {r.tipo_precio === 'mayorista' ? (
                          <span className="me-pill me-pill-mayor" style={{ fontSize: 10 }}>precio mayorista</span>
                        ) : (
                          <span style={{ fontFamily: 'var(--me-mono)', fontSize: 12, fontWeight: 700, color: 'var(--me-text)', fontFeatureSettings: '"tnum"' }}>
                            ${r.precio_especial}
                          </span>
                        )}
                        {r.descripcion && (
                          <span style={{ fontSize: 11.5, color: 'var(--me-text-4)' }}>{r.descripcion}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleEliminarRegla(r.id)}
                        className="me-icon-btn"
                        style={{ width: 26, height: 26, borderRadius: 6, flexShrink: 0 }}
                        disabled={eliminarRegla.isPending}
                      >
                        <TrashIcon size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {mostrarReglaForm && (
                <div style={{
                  border: '1px solid var(--me-line)', borderRadius: 8, padding: 12,
                  display: 'flex', flexDirection: 'column', gap: 10,
                  background: 'var(--me-surface-2)',
                }}>
                  <TwoCol>
                    <Field label="Cantidad mínima">
                      <input
                        className="me-input"
                        style={{ width: '100%', fontFamily: 'var(--me-mono)', fontSize: 13 }}
                        type="number" min="1" placeholder="Ej: 6"
                        value={reglaForm.cantidad_minima}
                        onChange={e => setReglaForm(f => ({ ...f, cantidad_minima: e.target.value }))}
                        autoFocus
                      />
                    </Field>
                    <Field label="Tipo">
                      <select
                        className="me-input"
                        style={{ width: '100%' }}
                        value={reglaForm.tipo_precio}
                        onChange={e => setReglaForm(f => ({ ...f, tipo_precio: e.target.value as 'mayorista' | 'personalizado' }))}
                      >
                        <option value="mayorista">Precio mayorista</option>
                        <option value="personalizado">Precio personalizado</option>
                      </select>
                    </Field>
                  </TwoCol>
                  {reglaForm.tipo_precio === 'personalizado' && (
                    <Field label="Precio especial">
                      <MoneyInput
                        value={parseFloat(reglaForm.precio_especial) || 0}
                        onChange={v => setReglaForm(f => ({ ...f, precio_especial: String(v) }))}
                        placeholder="0,00"
                      />
                    </Field>
                  )}
                  <Field label="Descripción (opcional)">
                    <input
                      className="me-input"
                      style={{ width: '100%' }}
                      type="text" placeholder="Ej: Pack x6, docena"
                      value={reglaForm.descripcion}
                      onChange={e => setReglaForm(f => ({ ...f, descripcion: e.target.value }))}
                    />
                  </Field>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="me-btn me-btn-ghost" style={{ flex: 1 }}
                      onClick={() => { setMostrarReglaForm(false); setReglaForm(REGLA_VACIA) }}>
                      Cancelar
                    </button>
                    <button type="button" className="me-btn me-btn-primary" style={{ flex: 2 }}
                      onClick={handleGuardarRegla} disabled={guardarRegla.isPending}>
                      {guardarRegla.isPending ? 'Guardando...' : 'Guardar regla'}
                    </button>
                  </div>
                </div>
              )}

              {!mostrarReglaForm && (
                <button
                  type="button"
                  className="me-btn me-btn-ghost"
                  style={{ alignSelf: 'flex-start', fontSize: 12, padding: '5px 12px' }}
                  onClick={() => setMostrarReglaForm(true)}
                >
                  + Agregar regla
                </button>
              )}
            </Section>

            <Section label="Ingredientes / Receta">
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--me-text-3)', lineHeight: 1.55 }}>
                Si este producto se arma con otros (ej: pancho = 1 salchicha + 1 pan),
                definí sus ingredientes aquí. Al venderse, el stock se descontará automáticamente
                de los ingredientes en lugar del producto final.
              </p>

              {ingredientes.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {ingredientes.map(ing => (
                    <div key={ing.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 10px', borderRadius: 8,
                      background: 'var(--me-surface-2)', border: '1px solid var(--me-line-2)',
                    }}>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 500, fontSize: 13, color: 'var(--me-text)' }}>
                          {ing.ingrediente_nombre}
                        </span>
                        {ing.ingrediente_sku && (
                          <span style={{ fontFamily: 'var(--me-mono)', fontSize: 11, color: 'var(--me-text-4)' }}>
                            {ing.ingrediente_sku}
                          </span>
                        )}
                        <span style={{ fontSize: 12, color: 'var(--me-text-3)' }}>×</span>
                        <span style={{ fontFamily: 'var(--me-mono)', fontSize: 13, fontWeight: 700, color: 'var(--me-text)', fontFeatureSettings: '"tnum"' }}>
                          {ing.cantidad}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="me-icon-btn"
                        style={{ width: 26, height: 26, borderRadius: 6, flexShrink: 0 }}
                        onClick={() => handleEliminarIngrediente(ing.id)}
                        disabled={eliminarIngrediente.isPending}
                      >
                        <TrashIcon size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {mostrarIngForm && (
                <div style={{
                  border: '1px solid var(--me-line)', borderRadius: 8, padding: 12,
                  display: 'flex', flexDirection: 'column', gap: 10,
                  background: 'var(--me-surface-2)',
                }}>
                  <TwoCol>
                    <Field label="Ingrediente">
                      <select
                        className="me-input"
                        style={{ width: '100%' }}
                        value={ingForm.ingrediente_id}
                        onChange={e => setIngForm(f => ({ ...f, ingrediente_id: e.target.value }))}
                        autoFocus
                      >
                        <option value="">Seleccionar...</option>
                        {todosProductos
                          .filter(p => p.id !== producto?.id && p.activo)
                          .map(p => (
                            <option key={p.id} value={p.id}>{p.nombre}</option>
                          ))
                        }
                      </select>
                    </Field>
                    <Field label="Cantidad">
                      <input
                        className="me-input"
                        style={{ width: '100%', fontFamily: 'var(--me-mono)', fontSize: 13 }}
                        type="number"
                        min="0.001"
                        step="0.001"
                        placeholder="Ej: 1"
                        value={ingForm.cantidad}
                        onChange={e => setIngForm(f => ({ ...f, cantidad: e.target.value }))}
                      />
                    </Field>
                  </TwoCol>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="me-btn me-btn-ghost" style={{ flex: 1 }}
                      onClick={() => { setMostrarIngForm(false); setIngForm(ING_VACIO) }}>
                      Cancelar
                    </button>
                    <button type="button" className="me-btn me-btn-primary" style={{ flex: 2 }}
                      onClick={handleAgregarIngrediente} disabled={agregarIngrediente.isPending}>
                      {agregarIngrediente.isPending ? 'Guardando...' : 'Agregar'}
                    </button>
                  </div>
                </div>
              )}

              {!mostrarIngForm && (
                <button
                  type="button"
                  className="me-btn me-btn-ghost"
                  style={{ alignSelf: 'flex-start', fontSize: 12, padding: '5px 12px' }}
                  onClick={() => setMostrarIngForm(true)}
                >
                  + Agregar ingrediente
                </button>
              )}
            </Section>
            </>
          )}
        </form>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--me-line)',
          display: 'flex', gap: 10,
          flexShrink: 0,
        }}>
          <button
            type="button"
            className="me-btn me-btn-ghost"
            onClick={onClose}
            style={{ flex: 1 }}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="me-btn me-btn-primary"
            style={{ flex: 2 }}
            disabled={isPending}
            onClick={handleSubmit}
          >
            {isPending ? 'Guardando...' : producto ? 'Guardar cambios' : 'Crear producto'}
          </button>
        </div>
      </div>
    </>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--me-text-3)',
        marginBottom: 12,
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {children}
      </div>
    </div>
  )
}

function TwoCol({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="me-label">{label}</label>
      {children}
    </div>
  )
}

function MoneyInput({ value, onChange, placeholder, name }: {
  value: number
  onChange: (v: number) => void
  placeholder?: string
  name?: string
}) {
  return (
    <div style={{ position: 'relative' }}>
      <span style={{
        position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
        color: 'var(--me-text-3)', fontSize: 13, pointerEvents: 'none',
      }}>$</span>
      <input
        id={name ? `prod-${name}` : undefined}
        name={name}
        className="me-input"
        style={{ width: '100%', paddingLeft: 24, fontFamily: 'var(--me-mono)', fontSize: 13 }}
        type="number"
        min="0"
        step="0.01"
        value={value === 0 ? '' : value}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        placeholder={placeholder}
      />
    </div>
  )
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}
